import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMeeting, API } from "../App";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Cable, ArrowLeft, ArrowRight, Info, CheckCircle2, ImageIcon, X, Settings } from "lucide-react";
import ImageLibraryManager from "../components/ImageLibraryManager";
import AssetConfigManager from "../components/AssetConfigManager";
import { usePresentationMode } from "../components/Layout";
import InteractiveColumns from "../components/InteractiveColumns";
import { CABLE_DATA, getSimpleCableComponents } from "../data/cableData";
import axios from "axios";

const DEFAULT_COMPONENTS = CABLE_DATA;

const CablePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { currentMeeting, toggleAsset, isAssetSelected } = useMeeting();
  const { isPresentationMode, enterPresentationMode } = usePresentationMode();
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [customImage, setCustomImage] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [components, setComponents] = useState(DEFAULT_COMPONENTS);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  useEffect(() => {
    if (searchParams.get('presentation') === 'true' && !isPresentationMode) {
      enterPresentationMode();
      searchParams.delete('presentation');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isPresentationMode, enterPresentationMode, setSearchParams]);

  useEffect(() => {
    fetchActiveImage();
    fetchConfig();
  }, []);

  const mergeImagesFromDefault = (customComponents, defaultComponents) => {
    return customComponents.map(customComp => {
      const defaultComp = defaultComponents.find(d => d.id === customComp.id || d.name === customComp.name);
      if (!defaultComp) return customComp;

      const mergedFailureModes = (customComp.failureModes || []).map(customFM => {
        const defaultFM = (defaultComp.failureModes || []).find(d => d.name === customFM.name);
        if (defaultFM && defaultFM.images && !customFM.images) {
          return { ...customFM, images: defaultFM.images };
        }
        return customFM;
      });

      const mergedTests = (customComp.tests || []).map(customTest => {
        const defaultTest = (defaultComp.tests || []).find(d => d.name === customTest.name);
        if (defaultTest && defaultTest.images && !customTest.images) {
          return { ...customTest, images: defaultTest.images };
        }
        return customTest;
      });

      const mergedMonitoring = (customComp.monitoring || []).map(customMon => {
        const defaultMon = (defaultComp.monitoring || []).find(d => d.name === customMon.name);
        if (defaultMon && defaultMon.images && !customMon.images) {
          return { ...customMon, images: defaultMon.images };
        }
        return customMon;
      });

      return {
        ...customComp,
        failureModes: mergedFailureModes,
        tests: mergedTests,
        monitoring: mergedMonitoring
      };
    });
  };

  const fetchConfig = async () => {
    try {
      setIsLoadingConfig(true);
      // Priority 1: Check asset-configs (user customizations with images)
      try {
        const configResp = await axios.get(`${API}/asset-configs/cables`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (configResp.data && configResp.data.components && configResp.data.components.length > 0) {
          const mergedComponents = mergeImagesFromDefault(configResp.data.components, DEFAULT_COMPONENTS);
          setComponents(mergedComponents);
          return;
        }
      } catch (e) { /* no custom config */ }
      // Priority 2: Check asset-types (admin-seeded dynamic config)
      try {
        const response = await axios.get(`${API}/asset-types/cables`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.components && response.data.components.length > 0) {
          const mergedComponents = mergeImagesFromDefault(response.data.components, DEFAULT_COMPONENTS);
          setComponents(mergedComponents);
          return;
        }
      } catch (e) { /* no dynamic config */ }
    } catch (error) {
      console.log("No custom cable config found, using defaults");
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const fetchActiveImage = async () => {
    try {
      setIsLoadingImage(true);
      const response = await axios.get(`${API}/asset-images/cables/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.url) {
        setCustomImage(response.data.url);
      }
    } catch (error) {
      console.log("No custom cable image found, using default");
    } finally {
      setIsLoadingImage(false);
    }
  };

  const handleComponentClick = (component) => {
    setSelectedComponent(selectedComponent?.id === component.id ? null : component);
  };

  const handleToggleAsset = (type, id) => {
    toggleAsset("Cables", type, id);
  };

  const handleSaveImage = async (imageUrl) => {
    try {
      await axios.post(`${API}/asset-images/cables`, 
        { url: imageUrl, isActive: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCustomImage(imageUrl);
    } catch (error) {
      console.error("Failed to save image:", error);
    }
  };

  const defaultCableImage = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800";

  return (
    <div className={`${isPresentationMode ? 'h-[calc(100vh-52px)] mt-[52px]' : 'h-full'} flex flex-col`}>
      {/* Header */}
      {!isPresentationMode && (
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Cable className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Cable Diagnostics</h1>
              <p className="text-slate-500">Power cable condition assessment and monitoring</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfigOpen(true)}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Configure
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsImageLibraryOpen(true)}
              className="gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              Change Image
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left Panel - Cable Diagram */}
        <div className={`${isPresentationMode ? 'w-1/3' : 'w-2/5'} flex flex-col`}>
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardHeader className="py-3 flex-shrink-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Cable className="w-5 h-5 text-purple-600" />
                Cable System Components
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-3 pt-0 overflow-hidden">
              {/* Cable Image */}
              <div className="relative mb-4 rounded-lg overflow-hidden bg-slate-100" style={{ height: '200px' }}>
                <img
                  src={customImage || defaultCableImage}
                  alt="Cable System"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = defaultCableImage;
                  }}
                />
                {!isPresentationMode && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-2 right-2 opacity-80 hover:opacity-100"
                    onClick={() => setIsImageLibraryOpen(true)}
                  >
                    <ImageIcon className="w-4 h-4 mr-1" />
                    Change
                  </Button>
                )}
              </div>

              {/* Component List */}
              <div className="flex-1 overflow-y-auto space-y-2">
                {components.map((component) => (
                  <div
                    key={component.id}
                    onClick={() => handleComponentClick(component)}
                    className={`p-3 rounded-lg cursor-pointer transition-all border-2 ${
                      selectedComponent?.id === component.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-transparent bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: component.color }}
                      />
                      <span className="font-medium text-slate-800">{component.name}</span>
                      <span className="text-xs text-slate-500 ml-auto">
                        {component.failureModes?.length || 0} failure modes
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Details */}
        <div className={`${isPresentationMode ? 'w-2/3' : 'w-3/5'} flex flex-col min-h-0`}>
          {selectedComponent ? (
            <InteractiveColumns
              component={selectedComponent}
              onToggleAsset={handleToggleAsset}
              isAssetSelected={isAssetSelected}
              assetType="Cables"
              isPresentationMode={isPresentationMode}
            />
          ) : (
            <Card className="flex-1 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <Cable className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">Select a cable component</p>
                <p className="text-sm">Click on a component to view failure modes, tests, and monitoring options</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      {!isPresentationMode && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => navigate("/assets/motors")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Motors
          </Button>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Info className="w-4 h-4" />
            <span>Click components to explore failure modes and recommended tests</span>
          </div>
          <Button
            onClick={() => navigate("/summary")}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            Session Summary
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Image Library Modal */}
      <ImageLibraryManager
        isOpen={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        assetType="cables"
        componentLabels={components.map(c => c.name)}
        onImageSaved={(imageData) => {
          if (imageData && imageData.url) {
            setCustomImage(imageData.url);
          }
        }}
      />

      {/* Configuration Modal */}
      <AssetConfigManager
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        assetType="cables"
        onConfigSaved={(config) => {
          if (config && config.components && config.components.length > 0) {
            const mergedComponents = mergeImagesFromDefault(config.components, DEFAULT_COMPONENTS);
            setComponents(mergedComponents);
          }
        }}
      />
    </div>
  );
};

export default CablePage;
